import { getStoredProfile } from '../services/authSession';
import { useState, useEffect } from 'react';

function generateRoomId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = 'fpt-';
  for (let i = 0; i < 8; i++) id += chars[Math.floor(Math.random() * chars.length)];
  return id;
}

export default function VideoCallPage() {
  const [roomId, setRoomId] = useState('');
  const [joinRoom, setJoinRoom] = useState('');
  const [activeRoom, setActiveRoom] = useState('');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    try {
      const profile = getStoredProfile();
      setUserName(profile.name || profile.hotel_name || 'Participant');
    } catch { /* ignore */ }
  }, []);

  const startCall = () => {
    const room = roomId || generateRoomId();
    setActiveRoom(room);
  };

  const joinExisting = () => {
    if (joinRoom.trim()) setActiveRoom(joinRoom.trim());
  };

  const endCall = () => setActiveRoom('');

  if (activeRoom) {
    const jitsiUrl = `https://meet.jit.si/${activeRoom}#userInfo.displayName="${encodeURIComponent(userName)}"&config.prejoinPageEnabled=false`;
    return (
      <div style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '8px 16px', background: 'white', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong>Vidéoconférence</strong>
            <span style={{ marginLeft: 12, fontSize: 13, color: '#6b7280' }}>Salle: {activeRoom}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => { navigator.clipboard.writeText(`https://meet.jit.si/${activeRoom}`); }}
              className="btn"
              style={{ fontSize: 12 }}
            >
              Copier le lien
            </button>
            <button onClick={endCall} className="btn" style={{ background: '#ef4444', color: 'white', border: 'none', fontSize: 12 }}>
              Terminer
            </button>
          </div>
        </div>
        <iframe
          src={jitsiUrl}
          style={{ flex: 1, border: 'none', width: '100%' }}
          allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div className="card">
        <h3>Vidéoconférence</h3>
        <div className="sub">
          Démarrez ou rejoignez une réunion vidéo avec les services ONT, ANR, CNS ou partenaires.
        </div>

        <div style={{ display: 'flex', gap: 24, marginTop: 24, flexWrap: 'wrap' }}>
          {/* Start new call */}
          <div style={{ flex: 1, minWidth: 280, padding: 20, borderRadius: 12, border: '1px solid #e5e7eb', background: '#f0fdf4' }}>
            <h4 style={{ margin: '0 0 8px' }}>Nouvelle réunion</h4>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
              Créez une salle et partagez le lien avec vos correspondants.
            </p>
            <input
              type="text"
              placeholder="Nom de la salle (optionnel)"
              value={roomId}
              onChange={e => setRoomId(e.target.value.replace(/[^a-zA-Z0-9-]/g, ''))}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 12 }}
            />
            <button className="btn primary" style={{ width: '100%' }} onClick={startCall}>
              Démarrer la réunion
            </button>
          </div>

          {/* Join existing */}
          <div style={{ flex: 1, minWidth: 280, padding: 20, borderRadius: 12, border: '1px solid #e5e7eb', background: '#eff6ff' }}>
            <h4 style={{ margin: '0 0 8px' }}>Rejoindre une réunion</h4>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
              Entrez le nom ou le lien de la salle pour rejoindre.
            </p>
            <input
              type="text"
              placeholder="Nom de salle ou lien meet.jit.si/..."
              value={joinRoom}
              onChange={e => {
                let v = e.target.value;
                if (v.includes('meet.jit.si/')) v = v.split('meet.jit.si/')[1]?.split(/[#?]/)[0] || '';
                setJoinRoom(v);
              }}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 12 }}
            />
            <button className="btn primary" style={{ width: '100%' }} onClick={joinExisting} disabled={!joinRoom.trim()}>
              Rejoindre
            </button>
          </div>
        </div>
      </div>

      {/* Quick-start institutional channels */}
      <div className="card">
        <h3>Canaux institutionnels</h3>
        <div className="sub">Salles permanentes pour les réunions régulières.</div>
        <div className="grid3" style={{ marginTop: 16 }}>
          {[
            { name: 'ONT — Collections', room: 'fpt-ont-collections', color: '#3b82f6' },
            { name: 'ONT — Support technique', room: 'fpt-ont-support', color: '#8b5cf6' },
            { name: 'CNS — Coordination', room: 'fpt-cns-coordination', color: '#10b981' },
            { name: 'ANR — Liaison', room: 'fpt-anr-liaison', color: '#f59e0b' },
            { name: 'ONT — Secrétariat général', room: 'fpt-ont-secretariat', color: '#6366f1' },
            { name: 'Réunion inter-services', room: 'fpt-inter-services', color: '#ec4899' },
          ].map(ch => (
            <div
              key={ch.room}
              onClick={() => setActiveRoom(ch.room)}
              style={{
                padding: 16, borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer',
                transition: 'box-shadow 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)')}
              onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: ch.color }} />
                <strong style={{ fontSize: 14 }}>{ch.name}</strong>
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Salle: {ch.room}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
